<!--
  @component SettingsModal
  @description Modal for configuring user preferences (item types and fields)
-->
<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { t } from '@/i18n';
  import { ITEM_TYPES, FIELD_LABELS, getFieldsForItemType } from '@/data';
  import {
    enabledItemTypes,
    setItemTypeEnabled,
    setEnabledItemTypes,
    getVisibleFields,
    setVisibleFields,
    resetFieldsForType,
    resetPreferences,
  } from '@stores/preferencesStore';
  import { notify } from '@services/notificationService';

  export let visible = false;
  export let onClose: () => void = () => {};

  type Tab = 'itemTypes' | 'fields';

  let activeTab: Tab = 'itemTypes';
  let selectedItemType: string = 'book';
  let modalElement: HTMLDivElement;

  // All available item types
  const allItemTypes = Object.entries(ITEM_TYPES).map(([key, config]) => ({
    value: key,
    label: config.label,
    icon: config.icon,
  }));

  // Fields for selected item type
  $: allFieldsForType = getFieldsForItemType(selectedItemType);
  $: visibleFields = getVisibleFields(selectedItemType);
  $: visibleFieldsSet = new Set(visibleFields);

  function handleClose() {
    onClose();
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      handleClose();
    }
  }

  function handleBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  }

  // Item type handlers
  function toggleItemType(itemType: string) {
    const isEnabled = $enabledItemTypes.includes(itemType);
    setItemTypeEnabled(itemType, !isEnabled);
  }

  function selectAllItemTypes() {
    setEnabledItemTypes(Object.keys(ITEM_TYPES));
  }

  function selectCommonItemTypes() {
    setEnabledItemTypes([
      'book',
      'bookSection',
      'journalArticle',
      'thesis',
      'conferencePaper',
      'report',
      'webpage',
      'document',
    ]);
  }

  // Field handlers
  function toggleField(field: string) {
    const currentFields = getVisibleFields(selectedItemType);
    const newFields = currentFields.includes(field)
      ? currentFields.filter((f) => f !== field)
      : [...currentFields, field];
    setVisibleFields(selectedItemType, newFields);
  }

  function selectAllFields() {
    setVisibleFields(selectedItemType, [...allFieldsForType]);
  }

  function selectEssentialFields() {
    const essential = ['title', 'creators', 'date', 'abstractNote', 'url', 'DOI', 'ISBN', 'ISSN'];
    const available = allFieldsForType.filter((f) => essential.includes(f));
    setVisibleFields(selectedItemType, available);
  }

  function handleResetFields() {
    resetFieldsForType(selectedItemType);
  }

  function handleResetAll() {
    if (confirm($t('settings.confirmReset'))) {
      resetPreferences();
      notify.success($t('settings.preferencesReset'));
    }
  }

  onMount(() => {
    document.addEventListener('keydown', handleKeydown);
  });

  onDestroy(() => {
    document.removeEventListener('keydown', handleKeydown);
  });
</script>

{#if visible}
  <div
    class="modal-backdrop"
    on:click={handleBackdropClick}
    on:keydown={handleKeydown}
    role="presentation"
  >
    <div class="modal" bind:this={modalElement}>
      <div class="modal-header">
        <h2 id="settings-modal-title">{$t('settings.title')}</h2>
        <button class="close-button" on:click={handleClose} aria-label={$t('common.close')}>
          &times;
        </button>
      </div>

      <!-- Tabs -->
      <div class="tabs">
        <button
          class="tab"
          class:active={activeTab === 'itemTypes'}
          on:click={() => (activeTab = 'itemTypes')}
        >
          {$t('settings.itemTypes')}
        </button>
        <button
          class="tab"
          class:active={activeTab === 'fields'}
          on:click={() => (activeTab = 'fields')}
        >
          {$t('settings.fields')}
        </button>
      </div>

      <div class="modal-body">
        {#if activeTab === 'itemTypes'}
          <!-- Item Types Tab -->
          <div class="tab-content">
            <p class="tab-description">{$t('settings.itemTypesDescription')}</p>

            <div class="quick-actions">
              <button class="btn btn-small" on:click={selectAllItemTypes}>
                {$t('settings.selectAll')}
              </button>
              <button class="btn btn-small" on:click={selectCommonItemTypes}>
                {$t('settings.selectCommon')}
              </button>
            </div>

            <div class="items-grid">
              {#each allItemTypes as itemType}
                <label
                  class="item-type-option"
                  class:enabled={$enabledItemTypes.includes(itemType.value)}
                >
                  <input
                    type="checkbox"
                    checked={$enabledItemTypes.includes(itemType.value)}
                    on:change={() => toggleItemType(itemType.value)}
                  />
                  <span class="item-icon">{itemType.icon}</span>
                  <span class="item-label">{$t(itemType.label)}</span>
                </label>
              {/each}
            </div>
          </div>
        {:else}
          <!-- Fields Tab -->
          <div class="tab-content">
            <p class="tab-description">{$t('settings.fieldsDescription')}</p>

            <!-- Item type selector -->
            <div class="field-type-selector">
              <label for="field-item-type">{$t('settings.selectItemType')}:</label>
              <select id="field-item-type" bind:value={selectedItemType}>
                {#each allItemTypes as itemType}
                  <option value={itemType.value}>
                    {itemType.icon}
                    {$t(itemType.label)}
                  </option>
                {/each}
              </select>
            </div>

            <div class="quick-actions">
              <button class="btn btn-small" on:click={selectAllFields}>
                {$t('settings.showAllFields')}
              </button>
              <button class="btn btn-small" on:click={selectEssentialFields}>
                {$t('settings.showEssential')}
              </button>
              <button class="btn btn-small" on:click={handleResetFields}>
                {$t('settings.resetToDefault')}
              </button>
            </div>

            <div class="fields-grid">
              {#each allFieldsForType as field}
                <label class="field-option" class:enabled={visibleFieldsSet.has(field)}>
                  <input
                    type="checkbox"
                    checked={visibleFieldsSet.has(field)}
                    on:change={() => toggleField(field)}
                  />
                  <span class="field-label">
                    {$t(FIELD_LABELS[field] || `item.fields.${field}`)}
                  </span>
                </label>
              {/each}
            </div>
          </div>
        {/if}
      </div>

      <div class="modal-footer">
        <button class="btn btn-danger" on:click={handleResetAll}>
          {$t('settings.resetAll')}
        </button>
        <button class="btn btn-primary" on:click={handleClose}>
          {$t('common.done')}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    animation: fadeIn 0.15s ease-out;
  }

  .modal {
    position: relative;
    background: var(--color-bg-secondary, #383838);
    border-radius: 8px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    width: 90vw;
    max-width: 700px;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    animation: slideIn 0.2s ease-out;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid var(--color-border, #555);
  }

  .modal-header h2 {
    margin: 0;
    font-size: 18px;
    color: var(--color-text-primary, #e8e8e8);
  }

  .close-button {
    background: none;
    border: none;
    font-size: 24px;
    color: var(--color-text-secondary, #aaa);
    cursor: pointer;
    padding: 0;
    line-height: 1;
  }

  .close-button:hover {
    color: var(--color-text-primary, #e8e8e8);
  }

  /* Tabs */
  .tabs {
    display: flex;
    border-bottom: 1px solid var(--color-border, #555);
  }

  .tab {
    flex: 1;
    padding: 12px 20px;
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    color: var(--color-text-secondary, #aaa);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .tab:hover {
    color: var(--color-text-primary, #e8e8e8);
    background: var(--color-bg-hover, #444);
  }

  .tab.active {
    color: var(--color-accent, #5c7cfa);
    border-bottom-color: var(--color-accent, #5c7cfa);
  }

  /* Body */
  .modal-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
  }

  .tab-content {
    min-height: 300px;
  }

  .tab-description {
    margin: 0 0 16px 0;
    font-size: 13px;
    color: var(--color-text-secondary, #aaa);
  }

  .quick-actions {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
  }

  /* Item types grid */
  .items-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 8px;
  }

  .item-type-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    background: var(--color-bg-primary, #2b2b2b);
    border: 1px solid var(--color-border, #555);
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s;
  }

  .item-type-option:hover {
    border-color: var(--color-accent, #5c7cfa);
  }

  .item-type-option.enabled {
    background: rgba(92, 124, 250, 0.1);
    border-color: var(--color-accent, #5c7cfa);
  }

  .item-icon {
    font-size: 16px;
  }

  .item-label {
    font-size: 13px;
    color: var(--color-text-primary, #e8e8e8);
  }

  /* Field type selector */
  .field-type-selector {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }

  .field-type-selector label {
    font-size: 14px;
    color: var(--color-text-secondary, #aaa);
  }

  .field-type-selector select {
    flex: 1;
    max-width: 300px;
    padding: 8px 12px;
    background: var(--color-bg-primary, #2b2b2b);
    border: 1px solid var(--color-border, #555);
    border-radius: 4px;
    color: var(--color-text-primary, #e8e8e8);
    font-size: 14px;
  }

  /* Fields grid */
  .fields-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 6px;
  }

  .field-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    background: var(--color-bg-primary, #2b2b2b);
    border: 1px solid var(--color-border, #555);
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s;
  }

  .field-option:hover {
    border-color: var(--color-accent, #5c7cfa);
  }

  .field-option.enabled {
    background: rgba(92, 124, 250, 0.1);
    border-color: var(--color-accent, #5c7cfa);
  }

  .field-label {
    font-size: 12px;
    color: var(--color-text-primary, #e8e8e8);
  }

  /* Footer */
  .modal-footer {
    display: flex;
    justify-content: space-between;
    padding: 16px 20px;
    border-top: 1px solid var(--color-border, #555);
  }

  /* Buttons */
  .btn {
    padding: 10px 16px;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .btn-small {
    padding: 6px 12px;
    font-size: 12px;
  }

  .btn-primary {
    background: var(--color-accent, #5c7cfa);
    border: 1px solid var(--color-accent, #5c7cfa);
    color: white;
  }

  .btn-primary:hover {
    background: var(--color-accent-hover, #4c6ef5);
  }

  .btn-danger {
    background: transparent;
    border: 1px solid var(--color-error, #ff6b6b);
    color: var(--color-error, #ff6b6b);
  }

  .btn-danger:hover {
    background: rgba(255, 107, 107, 0.1);
  }

  .btn-small {
    background: var(--color-bg-tertiary, #4a4a4a);
    border: 1px solid var(--color-border, #555);
    color: var(--color-text-primary, #e8e8e8);
  }

  .btn-small:hover {
    background: var(--color-bg-hover, #555);
  }

  /* Animations */
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  /* Responsive */
  @media (max-width: 600px) {
    .items-grid {
      grid-template-columns: 1fr;
    }

    .fields-grid {
      grid-template-columns: 1fr 1fr;
    }

    .quick-actions {
      flex-wrap: wrap;
    }
  }
</style>
